"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check, X, Sparkles, UserCheck, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Link } from "@/components/common/Link";
const comparisonCriteria = [{
  id: "strategy",
  feature: "Tailored SEO Strategy",
  description: "Bespoke roadmap built for your specific business goals and niche target audience.",
  yeConsultancy: "100% Custom strategy crafted directly by Yassine",
  yeCheck: true,
  diyTools: "Automated audit templates and standardized recommendation rules",
  diyCheck: false,
  agencies: "Standardized account-manager playbooks and tiered scopes",
  agencyCheck: false
}, {
  id: "access",
  feature: "Direct Expert Access",
  description: "Work directly with a senior SEO consultant with zero junior hand-offs.",
  yeConsultancy: "Direct WhatsApp & email access to Yassine",
  yeCheck: true,
  diyTools: "Help documentation, knowledge bases, and software support channels",
  diyCheck: false,
  agencies: "Junior account managers acting as client proxies",
  agencyCheck: false
}, {
  id: "implementation",
  feature: "Technical & AI Search Optimization",
  description: "Hands-on engineering for Google rankings and AI-powered search visibility.",
  yeConsultancy: "Done-for-you technical fixes & AI Search strategy",
  yeCheck: true,
  diyTools: "Diagnostic issue flags and crawl reports without code implementation",
  diyCheck: false,
  agencies: "Additional hourly fees or separate developer hand-offs for execution",
  agencyCheck: false
}, {
  id: "curve",
  feature: "Learning Curve & Time Required",
  description: "Amount of personal time required to manage and execute SEO activities.",
  yeConsultancy: "Zero learning curve — full done-for-you execution",
  yeCheck: true,
  diyTools: "Substantial internal time required to learn, interpret, and execute fixes",
  diyCheck: false,
  agencies: "Moderate overhead approving slow agency cycles and decks",
  agencyCheck: false
}, {
  id: "transparency",
  feature: "Transparent Reporting & ROI",
  description: "Clear metrics tied to qualified leads, revenue growth, and actual search impact.",
  yeConsultancy: "Live custom dashboard with clear business metrics",
  yeCheck: true,
  diyTools: "Standard metrics and rank tracking charts requiring in-house interpretation",
  diyCheck: false,
  agencies: "Vanity ranking reports without direct commercial attribution",
  agencyCheck: false
}, {
  id: "support",
  feature: "Agile Delivery & Honest Direction",
  description: "Direct feedback on what will actually drive organic revenue without lock-ins.",
  yeConsultancy: "Honest guidance, fast iterations & full asset ownership",
  yeCheck: true,
  diyTools: "Self-serve software platform without dedicated strategic consulting",
  diyCheck: false,
  agencies: "Rigid 12-month retainer contracts with set delivery cycles",
  agencyCheck: false
}];
const mobileColumns = [{
  id: "ye",
  label: "YE Consultancy",
  badge: "Recommended (Direct)"
}, {
  id: "diy",
  label: "DIY SEO Tools",
  badge: "Software Only"
}, {
  id: "agencies",
  label: "SEO Agencies",
  badge: "Outsourced Teams"
}];
export default function Comparison() {
  const [selectedMobileTab, setSelectedMobileTab] = useState<"ye" | "diy" | "agencies">("ye");
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };
  return <section id="toolcomparisonmatrix" data-nav="light" className="bg-background text-foreground relative overflow-hidden py-16 md:py-24 border-b border-border">
      <div className="absolute inset-0 pointer-events-none opacity-40 bg-[radial-gradient(var(--color-secondary)_1px,transparent_1px)] [background-size:24px_24px]" />

      <div className="container mx-auto px-4 sm:px-6 relative z-10 max-w-7xl">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4 bg-secondary text-secondary-foreground font-sans px-3 py-1">
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            <span>Honest SEO Comparison</span>
          </Badge>

          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground mb-4">
            Is DIY SEO Software Worth It? Comparing Tools, Agencies, and Direct Consulting
          </h2>

          <p className="font-sans text-muted-foreground text-base sm:text-lg leading-relaxed">
            DIY SEO software is a genuinely sensible, cost-effective choice if you have the internal bandwidth and technical appetite to interpret raw data and implement changes yourself. But when deciding whether software is enough or how it compares to hiring an agency or an independent consultant, here is an even-handed look at what each route actually delivers.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-12 bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-sm">
          <div className="lg:col-span-7 flex flex-col justify-center space-y-4">
            <div className="inline-flex items-center gap-2 text-primary font-semibold text-sm">
              <UserCheck className="w-5 h-5" />
              <span>Straightforward Guidance From Yassine</span>
            </div>
            <h3 className="font-serif text-2xl sm:text-3xl font-bold text-card-foreground">
              Choosing the right search model for your current stage of growth
            </h3>
            <p className="font-sans text-muted-foreground text-sm sm:text-base leading-relaxed">
              DIY tools are brilliant for early-stage teams wanting low-cost learning, while traditional agencies suit companies needing broad outsourced headcount. I work directly with businesses that have outgrown automated tooltips and want bespoke strategy, technical execution, and commercial results without agency markups.
            </p>
            <div className="pt-2 flex flex-wrap gap-3">
              <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
                <Link to="/ContactPage">
                  Book a Free Consultation
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="border-border hover:bg-muted font-medium">
                <Link to="/CaseStudiesPage">
                  Explore Case Studies
                </Link>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-5 flex items-center justify-center bg-secondary/30 rounded-xl p-6 border border-secondary">
            <div className="space-y-4 text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start gap-3">
                <img src="https://mighty-wolf.10web.cloud/wp-content/uploads/2026/08/SEO-Consultant-Freelancer.png" data-wvc-srcset="https://mighty-wolf.10web.cloud/wp-content/uploads/2026/08/SEO-Consultant-Freelancer.png" data-wvc-sizes="https://mighty-wolf.10web.cloud/wp-content/uploads/2026/08/SEO-Consultant-Freelancer.png" alt="Yassine Essaadi - Independent SEO Consultant" className="w-14 h-14 rounded-lg object-cover border-2 border-primary shadow-xs" loading="lazy" />
                <div>
                  <h4 className="font-serif font-bold text-foreground text-base">Yassine Essaadi</h4>
                  <p className="font-sans text-xs text-muted-foreground">Lead Freelance SEO Consultant</p>
                </div>
              </div>
              <blockquote className="font-sans text-sm text-foreground italic bg-background p-4 rounded-lg border border-border shadow-2xs">
                &ldquo;DIY tools are not inadequate — for many founders, they are the smartest place to start. I step in when you need senior strategic clarity, direct technical fixes, and commercial accountability without paying for agency fluff.&rdquo;
              </blockquote>
            </div>
          </div>
        </motion.div>

        <div className="hidden md:block overflow-hidden rounded-2xl border border-border shadow-sm bg-card">
          <Table className="w-full text-left border-collapse">
            <TableHeader className="bg-muted/60 border-b border-border">
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-1/3 py-5 px-6 font-serif font-bold text-foreground text-base">
                  Key SEO Criteria
                </TableHead>
                <TableHead className="w-1/4 py-5 px-6 font-serif font-bold text-primary text-base bg-secondary/40 border-x border-border">
                  <div className="flex items-center justify-between">
                    <span>YE Consultancy</span>
                    <Badge className="bg-primary text-primary-foreground text-2xs uppercase tracking-wider px-2 py-0.5">
                      Direct Expert
                    </Badge>
                  </div>
                </TableHead>
                <TableHead className="w-1/5 py-5 px-6 font-serif font-bold text-muted-foreground text-base">
                  Traditional DIY Tools
                </TableHead>
                <TableHead className="w-1/5 py-5 px-6 font-serif font-bold text-muted-foreground text-base">
                  General Agencies
                </TableHead>
              </TableRow>
            </TableHeader>

            <TableBody className="divide-y divide-border">
              {comparisonCriteria.map((item, i) => <TableRow data-index={i} key={item.id} className="hover:bg-muted/30 transition-colors">
                  <TableCell className="py-5 px-6 font-sans">
                    <div className="font-serif font-semibold text-foreground text-base mb-1">
                      {item.feature}
                    </div>
                    <div className="text-xs text-muted-foreground max-w-sm">
                      {item.description}
                    </div>
                  </TableCell>

                  <TableCell className="py-5 px-6 font-sans bg-secondary/20 border-x border-border">
                    <div className="flex items-start gap-2.5">
                      <div className="mt-0.5 p-1 rounded-lg bg-primary text-primary-foreground shrink-0 shadow-2xs">
                        <Check className="w-3.5 h-3.5" />
                      </div>
                      <span className="font-medium text-foreground text-sm">
                        {item.yeConsultancy}
                      </span>
                    </div>
                  </TableCell>

                  <TableCell className="py-5 px-6 font-sans">
                    <div className="flex items-start gap-2.5">
                      <div className="mt-0.5 p-1 rounded-lg bg-muted text-muted-foreground shrink-0">
                        <X className="w-3.5 h-3.5" />
                      </div>
                      <span className="text-muted-foreground text-sm">
                        {item.diyTools}
                      </span>
                    </div>
                  </TableCell>

                  <TableCell className="py-5 px-6 font-sans">
                    <div className="flex items-start gap-2.5">
                      <div className="mt-0.5 p-1 rounded-lg bg-muted text-muted-foreground shrink-0">
                        <X className="w-3.5 h-3.5" />
                      </div>
                      <span className="text-muted-foreground text-sm">
                        {item.agencies}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>)}
            </TableBody>
          </Table>
        </div>

        <div className="block md:hidden">
          <div className="flex items-center justify-between p-1.5 bg-muted rounded-xl mb-6 border border-border">
            {mobileColumns.map((col, i) => <Button key={col.id} data-index={i} onClick={() => setSelectedMobileTab(col.id as "ye" | "diy" | "agencies")} variant={selectedMobileTab === col.id ? "default" : "ghost"} size="sm" className={`flex-1 text-xs py-2 h-auto font-sans font-medium transition-all ${selectedMobileTab === col.id ? "bg-primary text-primary-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"}`}>
                {col.label}
              </Button>)}
          </div>

          <div className="space-y-4">
            {comparisonCriteria.map((item, i) => {
            const isYE = selectedMobileTab === "ye";
            const content = selectedMobileTab === "ye" ? item.yeConsultancy : selectedMobileTab === "diy" ? item.diyTools : item.agencies;
            const hasCheck = selectedMobileTab === "ye";
            return <Card key={item.id} data-index={i} className={`border transition-all ${isYE ? "border-primary bg-secondary/30 shadow-md" : "border-border bg-card"}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between gap-2">
                      <CardTitle className="font-serif text-base font-bold text-foreground">
                        {item.feature}
                      </CardTitle>
                      {isYE && <Badge className="bg-primary text-primary-foreground text-3xs uppercase">
                          Recommended
                        </Badge>}
                    </div>
                    <CardDescription className="text-xs text-muted-foreground font-sans">
                      {item.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-2">
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-background border border-border">
                      <div className={`mt-0.5 p-1 rounded-lg shrink-0 ${hasCheck ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                        {hasCheck ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                      </div>
                      <span className={`text-sm font-sans font-medium ${hasCheck ? "text-foreground" : "text-muted-foreground"}`}>
                        {content}
                      </span>
                    </div>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="mt-12 text-center bg-muted/50 border border-border rounded-2xl p-6 sm:p-10">
          <div className="max-w-2xl mx-auto space-y-4">
            <h3 className="font-serif text-xl sm:text-2xl font-bold text-foreground">
              Ready for SEO Strategy Without the Agency Bloat?
            </h3>
            <p className="font-sans text-sm sm:text-base text-muted-foreground">
              Book a direct consultation with Yassine to review your technical search strategy, discuss growth targets, and explore custom SEO deliverables.
            </p>
            <div className="pt-2 flex justify-center">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-8">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}